import {
  Wallet,
  Search,
  ListFilter,
  ArrowLeft,
  LogOut,
  ChevronDown
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import NetworkSelector from './NetworkSelector'
import { useWallet } from '@/hooks/useWallet'
import { useTranslation } from 'react-i18next'
import WalletManager from '@/services/wallet/wallet-manager'
import { getAllNetworks } from '@/config/networks'
import LanguageSelector from '@/components/common/LanguageSelector'

const Header = ({
  hideSidebar = false,
  showBackButton = false,
  onBack
}: {
  hideSidebar?: boolean
  showBackButton?: boolean
  onBack?: () => void
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { address, getAllWallets } = useWallet()

  const walletName = address
    ? getAllWallets().wallets.find((w) => w.address === address)?.name
    : ''

  const walletManager = WalletManager.getInstance()
  const handleLogout = () => {
    walletManager.lock()
    sessionStorage.removeItem('wallet_privatekey')
    sessionStorage.removeItem('react_wallet_password')
    localStorage.setItem('wallet_requires_auth', 'true')
    window.location.href = '/'
  }

  const handleBackClick = () => {
    // If on registration page, dispatch custom event
    if (pathname === '/registration') {
      window.dispatchEvent(new CustomEvent('registration-back'))
    } else if (pathname === '/') {
      onBack?.()
    } else {
      navigate(-1)
    }
  }

  // Routes where default header should be hidden
  const hideDefaultHeader = ['/registration', '/', '/reset-wallet']
  const hideDefaultHeaderMobileOnly = [
    '/assets',
    '/networks',
    '/add-network',
    '/wallets',
    '/add-wallet'
  ]
  const shouldHideDefaultHeader = hideDefaultHeader.includes(pathname)
  const isNetworkDetailsRoute = pathname.startsWith('/network-details/')
  const shouldHideDefaultHeaderMobile =
    hideDefaultHeaderMobileOnly.includes(pathname) || isNetworkDetailsRoute

  const networkDetailsName = isNetworkDetailsRoute
    ? getAllNetworks().find((n) => n.id === pathname.split('/').pop())?.name
    : undefined

  const formatAddress = (addr: string) => {
    if (!addr) return t('header.no_wallet')
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`
  }

  return (
    <header
      className={`fixed top-0 right-0 left-0 ${hideSidebar ? '' : 'lg:ml-64'} z-30 ${(pathname === '/' || pathname === '/reset-wallet') && !showBackButton ? '' : 'backdrop-blur-sm bg-card border-b border-border-base'}`}
    >
      <div className="flex items-center gap-2 px-6 py-4.5">
        {/* DEFAULT HEADER - Hidden on specific routes */}
        {!shouldHideDefaultHeader && (
          <div
            className={`flex flex-row gap-4 items-center w-full ${shouldHideDefaultHeaderMobile ? 'hidden lg:flex' : ''}`}
          >
            <img
              src="/assets/omd-wallet.svg"
              alt="OMD Wallet"
              className="w-10 h-10 lg:hidden"
            />
            <div className="w-px h-8 bg-border-base lg:hidden" />
            {address ? (
              <button
                onClick={() => navigate('/wallets')}
                className="flex flex-row gap-2 items-center hover:opacity-80 transition-opacity cursor-pointer"
              >
                <Wallet className="w-5 h-5 icon-gradient-secondary" />
                <h3 className="text-sm sm:text-xl text-text-main font-bold leading-snug tracking-tight">
                  {walletName || formatAddress(address)}
                </h3>
                <ChevronDown className="w-4 h-4 text-text-secondary" />
              </button>
            ) : (
              <button className="btn-gradient-fill rounded-full py-1.5 lg:py-2 px-6">
                <p className="font-extrabold">{t('header.connect_wallet')}</p>
              </button>
            )}

            <div className="grow" />
            <div className="gap-4 flex items-center">
              <NetworkSelector />
              <button
                onClick={handleLogout}
                className="lg:hidden p-2 text-accent hover:bg-white/10 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                aria-label={t('settings.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/*         {pathname === '/trading' && (
          <>
            <div className="flex flex-row gap-2 items-center">
              <ChartCandlestick className="w-5 h-5 icon-gradient" />
              <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
                Market
              </h3>
            </div>
            <div className="flex-1" />
            <ScanQrCode className="w-5 h-5 text-text-secondary cursor-pointer" />
          </>
        )} */}

        {/* ASSETS MOBILE HEADER */}
        {pathname === '/assets' && (
          <div className="flex flex-row w-full justify-between items-center lg:hidden">
            <ArrowLeft
              className="w-5 h-5 icon-gradient"
              onClick={handleBackClick}
            />
            <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
              {t('sidebar.assets')}
            </h3>
            <div className="flex gap-2">
              <Search className="w-5 h-5 icon-gradient cursor-pointer" />
              <ListFilter className="w-5 h-5 text-accent dark:text-primary cursor-pointer" />
            </div>
          </div>
        )}
        {/* NETWORKS MOBILE HEADER */}
        {pathname === '/networks' && (
          <div className="flex flex-row w-full justify-between items-center lg:hidden">
            <ArrowLeft
              className="w-5 h-5 icon-gradient"
              onClick={handleBackClick}
            />
            <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
              {t('networks.title')}
            </h3>
            <div className="w-5" />
          </div>
        )}
        {/* ADD NETWORK MOBILE HEADER */}
        {pathname === '/add-network' && (
          <div className="flex flex-row w-full justify-between items-center lg:hidden">
            <ArrowLeft
              className="w-5 h-5 icon-gradient"
              onClick={handleBackClick}
            />
            <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
              {t('add_network.title')}
            </h3>
            <div className="w-5" />
          </div>
        )}
        {/* WALLETS MOBILE HEADER */}
        {pathname === '/wallets' && (
          <div className="flex flex-row w-full justify-between items-center lg:hidden">
            <ArrowLeft
              className="w-5 h-5 icon-gradient"
              onClick={handleBackClick}
            />
            <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
              {t('wallets.title')}
            </h3>
            <div className="w-5" />
          </div>
        )}
        {/* ADD WALLET MOBILE HEADER */}
        {pathname === '/add-wallet' && (
          <div className="flex flex-row w-full justify-between items-center lg:hidden">
            <ArrowLeft
              className="w-5 h-5 icon-gradient"
              onClick={handleBackClick}
            />
            <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
              {t('wallets.add_title')}
            </h3>
            <div className="w-5" />
          </div>
        )}
        {/* NETWORK DETAILS MOBILE HEADER */}
        {isNetworkDetailsRoute && (
          <div className="flex flex-row w-full justify-between items-center lg:hidden">
            <ArrowLeft
              className="w-5 h-5 icon-gradient"
              onClick={handleBackClick}
            />
            <h3 className="text-xl text-text-main font-bold leading-snug tracking-tight">
              {networkDetailsName || t('add_network.title')}
            </h3>
            <div className="w-5" />
          </div>
        )}
        {/*REGISTRATION HEADER */}
        {pathname === '/registration' && (
          <div className="flex flex-row w-full justify-between items-center">
            <div className="flex gap-4 items-center">
              <ArrowLeft
                className="w-5 h-5 icon-gradient cursor-pointer"
                onClick={handleBackClick}
              />

              <img
                src="/assets/omd-wallet.svg"
                alt="OMD Wallet"
                className="w-10 h-10"
              />
            </div>

            <LanguageSelector />

            {/* <div className="grow" />
            <MessageCircleQuestionMark className="w-5 h-5 icon-gradient" /> */}
          </div>
        )}
        {/* WELCOME HEADER */}
        {(pathname === '/' || pathname === '/reset-wallet') && (
          <div className="flex flex-row w-full justify-between items-center">
            {showBackButton ? (
              <ArrowLeft
                className="w-5 h-5 icon-gradient cursor-pointer"
                onClick={handleBackClick}
              />
            ) : (
              <div className="w-5" />
            )}

            <LanguageSelector />
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
